import { AlertTriangle, PencilLine, Search } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import i18n, { currentLocale } from '@/lib/i18n';
import { en, he, NAMESPACES } from '@/locales';

import {
  useContentOverrides,
  useRevertContent,
  useSaveContent,
  type ContentEntry,
  type ContentLocale,
} from './content-api';

/* ---------------------------------------------------------------------------
   /admin ← תוכן. כל טקסט באתר — דף הנחיתה, תנאים, פרטיות והממשק עצמו — הוא
   מפתח בקבצי התרגום. כאן בוחרים מפתח, כותבים נוסח חדש, והוא נשמר בשרת ודורס
   את מה שבקוד אצל כל המבקרים (בלי פריסה). "החזרה למקור" מוחקת את הדריסה.
   --------------------------------------------------------------------------- */

const SOURCES: Record<ContentLocale, Record<string, unknown>> = { he, en };

/** האזורים שהכי סביר שירצו לערוך — ראשונים ברשימה. */
const FIRST = ['public', 'auth', 'onboarding', 'common', 'dashboard', 'assistant'];
const ORDERED = [
  ...FIRST.filter((n) => (NAMESPACES as readonly string[]).includes(n)),
  ...NAMESPACES.filter((n) => !FIRST.includes(n)),
];

const PAGE = 60;

interface Row {
  key: string;
  original: string;
  override?: ContentEntry;
}

/** מפתחות מנוקדים לכל מחרוזת, כולל פריטים ברשימות ("legal.terms.sections.3.body.0"). */
export function flatten(tree: unknown, prefix = ''): Array<[string, string]> {
  if (typeof tree === 'string') return [[prefix, tree]];
  if (typeof tree !== 'object' || tree === null) return [];
  return Object.entries(tree).flatMap(([k, v]) => flatten(v, prefix ? `${prefix}.${k}` : k));
}

/** {{placeholder}} ותגיות <b>…</b> — חייבים להישאר כדי שהטקסט יוצג נכון. */
export function tokensOf(text: string): string[] {
  return [...text.matchAll(/\{\{\s*([\w.]+)[^}]*\}\}|<\/?([a-zA-Z]+)>/g)].map((m) =>
    m[0].replace(/\s+/g, ''),
  );
}

export function missingTokens(original: string, next: string): string[] {
  const have = tokensOf(next);
  return [...new Set(tokensOf(original))].filter((tok) => !have.includes(tok));
}

export function ContentSection() {
  const { t } = useTranslation('admin');
  const [locale, setLocale] = useState<ContentLocale>(() =>
    currentLocale() === 'en' ? 'en' : 'he',
  );
  const [ns, setNs] = useState<string>('public');
  const [search, setSearch] = useState('');
  const [onlyEdited, setOnlyEdited] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [editing, setEditing] = useState<Row | null>(null);
  const overrides = useContentOverrides(locale);

  const editedCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of overrides.data ?? [])
      counts.set(o.namespace, (counts.get(o.namespace) ?? 0) + 1);
    return counts;
  }, [overrides.data]);

  const rows = useMemo<Row[]>(() => {
    const byKey = new Map(
      (overrides.data ?? []).filter((o) => o.namespace === ns).map((o) => [o.key, o]),
    );
    const q = search.trim().toLowerCase();
    return flatten(SOURCES[locale][ns])
      .map(([key, original]) => ({ key, original, override: byKey.get(key) }))
      .filter((r) => !onlyEdited || r.override)
      .filter(
        (r) =>
          !q ||
          r.key.toLowerCase().includes(q) ||
          r.original.toLowerCase().includes(q) ||
          (r.override?.value.toLowerCase().includes(q) ?? false),
      );
  }, [overrides.data, locale, ns, search, onlyEdited]);

  const nsLabel = (n: string): string => {
    const key = `admin:content.namespaces.${n}`;
    return i18n.exists(key) ? String((i18n.t as (k: string) => string)(key)) : n;
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-fg-muted">{t('content.description')}</p>

      <div className="grid gap-3 sm:grid-cols-[8rem_minmax(0,16rem)_1fr]">
        <Field label={t('content.locale')} htmlFor="content-locale">
          <Select
            value={locale}
            onValueChange={(v) => {
              setLocale(v as ContentLocale);
              setLimit(PAGE);
            }}
          >
            <SelectTrigger id="content-locale">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="he">{t('lang.he')}</SelectItem>
              <SelectItem value="en">{t('lang.en')}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={t('content.area')} htmlFor="content-ns">
          <Select
            value={ns}
            onValueChange={(v) => {
              setNs(v);
              setLimit(PAGE);
            }}
          >
            <SelectTrigger id="content-ns">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ORDERED.map((n) => (
                <SelectItem key={n} value={n}>
                  {nsLabel(n)}
                  {editedCount.get(n)
                    ? ` · ${t('content.editedCount', { count: editedCount.get(n) })}`
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t('content.search')} htmlFor="content-search">
          <Input
            id="content-search"
            value={search}
            placeholder={t('content.searchPlaceholder')}
            onChange={(e) => {
              setSearch(e.target.value);
              setLimit(PAGE);
            }}
          />
        </Field>
      </div>

      <label className="flex w-fit items-center gap-2 text-sm text-fg-muted">
        <Checkbox checked={onlyEdited} onCheckedChange={(v) => setOnlyEdited(v === true)} />
        {t('content.onlyEdited')}
      </label>

      <Card className="overflow-hidden">
        {overrides.isError ? (
          <ErrorState error={overrides.error} onRetry={() => void overrides.refetch()} />
        ) : overrides.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Search} title={t('content.empty')} />
        ) : (
          <ul className="divide-y divide-border">
            {rows.slice(0, limit).map((r) => (
              <li key={r.key} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <code dir="ltr" className="truncate text-[11px] text-fg-subtle">
                      {r.key}
                    </code>
                    {r.override ? <Badge tone="accent">{t('content.edited')}</Badge> : null}
                  </div>
                  <p
                    dir={locale === 'he' ? 'rtl' : 'ltr'}
                    className="line-clamp-3 whitespace-pre-line text-sm text-fg"
                  >
                    {r.override?.value ?? r.original}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(r)}
                  aria-label={t('content.editKey', { key: r.key })}
                >
                  <PencilLine aria-hidden />
                  {t('content.edit')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {rows.length > limit ? (
          <div className="border-t border-border p-3 text-center">
            <Button variant="secondary" size="sm" onClick={() => setLimit((l) => l + PAGE)}>
              {t('content.more', { count: rows.length - limit })}
            </Button>
          </div>
        ) : null}
      </Card>

      {editing ? (
        <EditDialog locale={locale} namespace={ns} row={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}

function EditDialog({
  locale,
  namespace,
  row,
  onClose,
}: {
  locale: ContentLocale;
  namespace: string;
  row: Row;
  onClose: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const toast = useToast();
  const save = useSaveContent();
  const revert = useRevertContent();
  const [value, setValue] = useState(row.override?.value ?? row.original);
  const missing = missingTokens(row.original, value);
  const dir = locale === 'he' ? 'rtl' : 'ltr';
  const unchanged = value === (row.override?.value ?? row.original);

  const fail = (err: unknown) =>
    toast.error(t('content.failed'), err instanceof ApiError ? err.message : undefined);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (unchanged) return onClose();
    // חזרה לנוסח המקורי בדיוק = מחיקת הדריסה, לא שמירת עותק שלו.
    if (value === row.original && row.override) {
      revert.mutate(
        { id: row.override.id, locale },
        {
          onSuccess: () => {
            toast.success(t('content.reverted'));
            onClose();
          },
          onError: fail,
        },
      );
      return;
    }
    save.mutate(
      { locale, namespace, key: row.key, value },
      {
        onSuccess: () => {
          toast.success(t('content.saved'));
          onClose();
        },
        onError: fail,
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="content-edit-desc" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('content.editTitle')}</DialogTitle>
          <DialogDescription id="content-edit-desc">
            <code dir="ltr" className="text-xs">
              {namespace}:{row.key}
            </code>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate>
          <DialogBody>
            <div className="space-y-1">
              <p className="text-xs font-medium text-fg-muted">{t('content.original')}</p>
              <p
                dir={dir}
                className="whitespace-pre-line rounded-(--radius-md) bg-surface-sunken p-3 text-sm text-fg-muted"
              >
                {row.original}
              </p>
            </div>
            <Field label={t('content.newText')} htmlFor="content-value" hint={t('content.hint')}>
              <Textarea
                id="content-value"
                dir={dir}
                autoFocus
                rows={Math.min(14, Math.max(3, Math.ceil(value.length / 70)))}
                maxLength={20000}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </Field>
            {missing.length > 0 ? (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-(--radius-md) border border-warning-border bg-warning-subtle p-3 text-xs text-warning"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  {t('content.missingTokens')} <code dir="ltr">{missing.join('  ')}</code>
                </span>
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            {row.override ? (
              <Button
                type="button"
                variant="ghost"
                className="me-auto"
                loading={revert.isPending}
                onClick={() =>
                  revert.mutate(
                    { id: row.override!.id, locale },
                    {
                      onSuccess: () => {
                        toast.success(t('content.reverted'));
                        onClose();
                      },
                      onError: fail,
                    },
                  )
                }
              >
                {t('content.revert')}
              </Button>
            ) : null}
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t('common:actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" loading={save.isPending} disabled={value.trim() === ''}>
              {t('content.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
